// Methodology Orientation Panel — sits at the top of /admin/xray-cohort.
// Two states: always-visible collapsed view + modal "Read more" expanded view.
// Content per the THCO methodology brief; visual contract follows the
// established Sagicor palette + v4.0 forest tokens.

import React, { useEffect, useState } from "react";
import { Lightbulb, X } from "lucide-react";

// Palette tokens (mirror of XrayTemplateExtensions.C for visual consistency)
const C = {
  forest:      "#00583D",
  forestDark:  "#0A3E2A",
  forestPale:  "#E8F0EC",
  ivory:       "#FAFAF7",
  gold:        "#C28F2C",
  charcoal:    "#1F2933",
  charcoalSoft:"#3A4350",
  line:        "#D8DDE3",
  muted:       "#6C7480",
};
const FONT_HEAD = '"Cambria", "Georgia", serif';
const FONT_BODY = '"Calibri", "Segoe UI", system-ui, sans-serif';

// ──────────────────────────────────────────────────────────────────────
// Collapsed view (always visible)
// ──────────────────────────────────────────────────────────────────────
export default function MethodologyPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid="methodology-panel"
      style={{
        background: C.forestPale,
        border: `1px solid ${C.line}`,
        borderBottom: `2px solid ${C.line}`,
        borderRadius: 8,
        padding: "14px 18px",
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 14,
        fontFamily: FONT_BODY,
      }}
    >
      <Lightbulb size={18} strokeWidth={2} color={C.gold} aria-hidden style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: FONT_HEAD,
            fontSize: 14,
            fontWeight: 700,
            color: C.forestDark,
            letterSpacing: 0.2,
            marginBottom: 4,
          }}
        >
          About this assessment
        </div>
        <div style={{ fontSize: 11, color: C.charcoal, lineHeight: 1.55 }}>
          This is the Sagicor Technology Capability Assessment platform. Each candidate in the cohort below has been evaluated across five layers of evidence and rendered as a Candidate X-Ray containing their placement, the evidence behind it, and a calibrated Personal Development Plan. The methodology is evidence-led, cadre-relative, and framed as investment in development throughout.
        </div>
      </div>
      <button
        data-testid="methodology-read-more-btn"
        onClick={() => setOpen(true)}
        onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
        style={{
          flexShrink: 0,
          background: "transparent",
          border: 0,
          color: C.forest,
          fontFamily: FONT_BODY,
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          padding: "4px 8px",
          whiteSpace: "nowrap",
        }}
      >
        Read more on the methodology →
      </button>
      {open && <MethodologyModal onClose={() => setOpen(false)} />}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Expanded modal view
// ──────────────────────────────────────────────────────────────────────
function MethodologyModal({ onClose }) {
  // ESC to close + body-scroll lock
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const stop = (e) => e.stopPropagation();

  return (
    <div
      data-testid="methodology-modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(31, 41, 51, 0.40)",
        zIndex: 2000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "60px 20px",
        overflowY: "auto",
      }}
    >
      <div
        data-testid="methodology-modal"
        onClick={stop}
        role="dialog"
        aria-modal="true"
        aria-labelledby="methodology-modal-title"
        style={{
          width: "100%",
          maxWidth: 720,
          background: C.ivory,
          borderRadius: 10,
          boxShadow: "0 20px 60px rgba(0,0,0,0.30)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 120px)",
          fontFamily: FONT_BODY,
          color: C.charcoal,
        }}
      >
        {/* Header band */}
        <div
          style={{
            background: C.forest,
            color: "#FFFFFF",
            padding: "20px 28px",
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            flexShrink: 0,
          }}
        >
          <h2
            id="methodology-modal-title"
            style={{
              fontFamily: FONT_HEAD,
              fontSize: 16,
              fontWeight: 700,
              margin: 0,
              letterSpacing: 0.3,
            }}
          >
            The Sagicor Technology Capability Assessment
          </h2>
          <div style={{ fontFamily: FONT_BODY, fontSize: 11, fontStyle: "italic", marginTop: 4, opacity: 0.92 }}>
            How the assessment works, at a glance
          </div>
        </div>

        {/* Body — scrollable */}
        <div style={{ overflowY: "auto", padding: "22px 28px", flex: 1 }}>
          <Section n="Section 1" title="The Question the Assessment Answers">
            <p style={pStyle}>The assessment is built to answer one question per candidate:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>What does the evidence actually show about this person's current capability</li>
              <li style={liStyle}>What is the realistic next step in their development</li>
              <li style={liStyle}>How does their evidence map against the structure Sagicor is moving toward</li>
            </ul>
            <p style={pStyle}>The assessment is not built to render verdicts on whether someone is good or bad. It is not built to make placement decisions. Those conversations happen downstream, with the assessment as input.</p>
          </Section>

          <Divider />

          <Section n="Section 2" title="Five Layers of Evidence">
            <p style={pStyle}>Each candidate is evaluated across five distinct layers. Four contribute to a composite score; the fifth is diagnostic only.</p>
            <ul style={ulStyle}>
              <li style={liStyle}><B>Self-Assessment</B>, The candidate's own perspective. Diagnostic only. Surfaces under-raters, hidden talent, and aspiration-versus-capability gaps.</li>
              <li style={liStyle}><B>Manager Validation</B>, The direct manager's perspective. Highest weight because the manager has the densest observed evidence.</li>
              <li style={liStyle}><B>Technical Assessment</B>, A three-phase evaluation weighted differently by cadre. What counts as evidence of capability differs by role level.</li>
              <li style={liStyle}><B>Hands-On Simulation</B>, Tests whether the candidate executes under realistic constraints.</li>
              <li style={liStyle}><B>Tools and Skills Adjacency</B>, Catches the pattern of strong candidates whose tooling has fallen behind their role.</li>
            </ul>
            <p style={pStyle}>The four scored layers combine into a <B>Builder Readiness Index</B> on a 0-to-100 scale.</p>
          </Section>

          <Divider />

          <Section n="Section 3" title="Observed Versus Inferred, and Confidence">
            <p style={pStyle}>Every placement carries two protections against being read with more certainty than the evidence supports.</p>
            <ul style={ulStyle}>
              <li style={liStyle}><B>Observed evidence</B> is rendered in forest green on every X-Ray. <B>Inferred evidence</B> is rendered in muted gold. The colour distinction is mandatory.</li>
              <li style={liStyle}>
                Every placement carries a <B>Confidence Rating</B> based on how many of the four layers were directly observed:
                <ul style={subUlStyle}>
                  <li style={liStyle}>HIGH, all four layers observed</li>
                  <li style={liStyle}>MEDIUM, three layers observed</li>
                  <li style={liStyle}>LOW, two layers observed</li>
                  <li style={liStyle}>VERY LOW, one layer observed</li>
                </ul>
              </li>
            </ul>
            <p style={pStyle}>Two candidates with the same Builder Readiness Index are not necessarily equally placed. The X-Ray makes this distinction visible so any reader can see how much weight to give the placement at a glance.</p>
          </Section>

          <Divider />

          <Section n="Section 4" title="Cadre and Skill Domain">
            <p style={pStyle}>Each candidate is classified across two further dimensions.</p>
            <p style={pStyle}><B>Cadre</B>, The level of work the person is sized for, mapped to the Skills Framework for the Information Age:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>Apprentice</li>
              <li style={liStyle}>Practitioner-IC</li>
              <li style={liStyle}>Senior IC</li>
              <li style={liStyle}>Architect-track</li>
              <li style={liStyle}>Executive-track</li>
            </ul>
            <p style={pStyle}><B>Skill domain</B>, The kind of work the person does:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>Integrator (cross-functional engineering)</li>
              <li style={liStyle}>DevSecOps (platform, infrastructure, security)</li>
              <li style={liStyle}>BOAT (Business Operations and Adjacent Technology)</li>
              <li style={liStyle}>Data &amp; AI</li>
            </ul>
            <p style={pStyle}>Placement is read <B>cadre-relative</B> throughout. A Strong rating from a Practitioner-IC is held against a different standard than a Strong rating from an Architect-track candidate. This is the central protection against weaponising senior expectations against junior candidates, or hiding senior shortfalls behind junior standards.</p>
          </Section>

          <Divider />

          <Section n="Section 5" title="Tier, Verdict, and Disposition">
            <p style={pStyle}>Each candidate lands on three separate constructs, each answering a different question.</p>
            <p style={pStyle}><B>Tier</B>, How strongly the candidate is performing at their cadre level. Six tiers:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>Core Builder</li>
              <li style={liStyle}>Emerging Builder</li>
              <li style={liStyle}>Support Developer</li>
              <li style={liStyle}>Support Developer (PRR)</li>
              <li style={liStyle}>Restructuring Candidate</li>
              <li style={liStyle}>No Data</li>
            </ul>
            <p style={pStyle}><B>Verdict</B>, What action the placement triggers:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>Stabilise &amp; Monitor</li>
              <li style={liStyle}>Redeploy</li>
              <li style={liStyle}>Transition</li>
              <li style={liStyle}>Defer (Platform Issue)</li>
            </ul>
            <p style={pStyle}><B>Disposition</B>, What the workforce planning conversation looks like:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>Retain &amp; Elevate</li>
              <li style={liStyle}>Develop</li>
              <li style={liStyle}>Reassign</li>
              <li style={liStyle}>Transition</li>
            </ul>
            <p style={pStyle}>Three constructs, three lenses. The reader can disagree with the action without disagreeing with the capability placement, or the other way around.</p>
          </Section>

          <Divider />

          <Section n="Section 6" title="The Candidate X-Ray">
            <p style={pStyle}>The output for each candidate is a single visual rendering called the Candidate X-Ray. Each X-Ray contains:</p>
            <ul style={ulStyle}>
              <li style={liStyle}><B>Header strip</B>, name, cadre, tier, region, manager, skill domain</li>
              <li style={liStyle}><B>Intelligence panel</B>, six cards summarising the placement at a glance</li>
              <li style={liStyle}><B>Verdict panel</B>, the verdict label, pill, and two-sentence narrative</li>
              <li style={liStyle}><B>Strategic Assessment diagram</B>, four-quadrant rendering of current state, capability gaps, strategic value, and recommendation</li>
              <li style={liStyle}><B>Builder Path diagram</B>, horizontal timeline of progression from current placement to next-tier target</li>
              <li style={liStyle}><B>Investment Training Roadmap</B>, stacked credential blocks per named skill gap, no dollar amounts</li>
              <li style={liStyle}><B>Three integrated visualisations</B>, Score Composition donut, Four-Lens Audit radar, AI Posture matrix</li>
              <li style={liStyle}><B>Technical Competency Detail table</B>, every relevant skill with proficiency, requirement, gap status, and source attribution</li>
              <li style={liStyle}><B>Recommendation Layer</B>, the candidate's fit against the four functions in the new operating structure</li>
              <li style={liStyle}><B>Personal Development Plan</B>, twenty lettered sections (A through T) calibrated per candidate</li>
            </ul>
          </Section>

          <Divider />

          <Section n="Section 7" title="AI Posture: How the Methodology Reads the AI Transition">
            <p style={pStyle}>The AI Posture is the methodology's read on how the candidate's work intersects with the AI transition. It is distinct from the capability question that the BRI and tier address. A candidate can be a strong Builder at their cadre and still carry High AI Displacement Risk if their work is the kind AI is replacing. A candidate can be at Support Developer tier and carry Low Displacement Risk if their work is judgement-protected. The two questions are independent and the X-Ray surfaces both.</p>
            <p style={pStyle}>Each candidate is plotted on two axes.</p>
            <p style={pStyle}><B>AI Displacement Risk</B> is a 0-to-100 score (lower is safer) reading how likely AI is to displace the work the candidate currently does. The composite reflects the blend across the candidate's role surface:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>Routine end-user support tasks: high displacement risk (AI tools automate these at scale today)</li>
              <li style={liStyle}>Engineering and operations tasks: moderate displacement risk (AI augments rather than replaces at the expert-judgement level)</li>
              <li style={liStyle}>Senior judgement-protected work (architecture, security strategy, executive decision-making): low displacement risk (work depends on synthesis and judgement AI does not currently substitute for)</li>
            </ul>
            <p style={pStyle}>Bands: Low (0 to 40), Moderate (40 to 70), High or Critical (70 to 100).</p>
            <p style={pStyle}><B>AI Embedding Readiness</B> is a four-band placement reading how integrated AI tools are into the candidate's current daily work:</p>
            <ul style={ulStyle}>
              <li style={liStyle}><B>At Risk</B>: limited tool literacy; significant ramp needed</li>
              <li style={liStyle}><B>Foundational</B>: basic AI tool literacy is present</li>
              <li style={liStyle}><B>Developing</B>: AI is being used informally and personally, but not yet team-embedded or documented</li>
              <li style={liStyle}><B>Ready Now</B>: AI is integrated into daily workflow, team-visible, and documented</li>
            </ul>
            <p style={pStyle}>The two axes combine into four quadrants:</p>
            <ul style={ulStyle}>
              <li style={liStyle}><B>SAFE &amp; READY</B> (top-left: low displacement and high readiness): the destination quadrant. Work AI does not threaten plus embedded AI practice. The AI champions for the function.</li>
              <li style={liStyle}><B>AUGMENT</B> (top-right: high displacement and high readiness): substantial AI exposure but already positioned to ride the wave. Role evolution expected; readiness is in place.</li>
              <li style={liStyle}><B>DEVELOP</B> (bottom-left: low displacement and low readiness): fundamentals first. Safe from displacement but AI capability has not yet been built. Development priority is to embed AI practice before scope expands.</li>
              <li style={liStyle}><B>RESKILL</B> (bottom-right: high displacement and low readiness): priority intervention. Work is exposed to AI displacement and AI readiness has not kept pace. The quadrant that triggers the most urgent development investment.</li>
            </ul>
            <p style={pStyle}>The X-Ray's AI Posture Matrix shows the candidate's quadrant position plus reference dots indicating where peer cohorts sit, so any reader can see the candidate's posture relative to their peers.</p>
          </Section>

          <Divider />

          <Section n="Section 8" title="The Four Functions in the New Operating Structure">
            <p style={pStyle}>The Recommendation Layer on every X-Ray maps the candidate against the four functions in the new operating structure:</p>
            <ul style={ulStyle}>
              <li style={liStyle}><B>Infrastructure &amp; Platforms</B>, Group-level function under the Group CTO. Cloud, infrastructure, platform services. Mandate is optimisation and cost discipline.</li>
              <li style={liStyle}><B>Enterprise Architecture &amp; Digital Design</B>, Group-level function under the Group Chief Architect. Architecture, digital design. The AI Accelerator reports here.</li>
              <li style={liStyle}><B>Cyber Security &amp; Technology Risk Management</B>, Group-level function under the Group CISO. Cybersecurity posture and technology risk management.</li>
              <li style={liStyle}><B>Business Growth &amp; Digital Enablement</B>, Business-facing function. Technology work that drives revenue, customer experience, and operational outcomes in each business segment.</li>
            </ul>
            <p style={pStyle}>The Recommendation Layer is explicitly <B>workforce planning input rather than a placement decision</B>, with that framing reminder visible on every X-Ray.</p>
          </Section>

          <Divider />

          <Section n="Section 9" title="Three Quality Gates Before Release">
            <p style={pStyle}>Every X-Ray passes through three automated quality tests before it is written to the database. The gate is strict; any failure on any test holds the record for re-generation rather than releasing it.</p>
            <ul style={ulStyle}>
              <li style={liStyle}><B>Schema test</B>, every required field present and conformant to the locked schema</li>
              <li style={liStyle}><B>Methodology test</B>, cadre, tier, verdict, and disposition combinations internally consistent; reconciliation rules correctly applied</li>
              <li style={liStyle}><B>Content test</B>, no prohibited language, no adverse framing, no dollar amounts, no manager quotes</li>
            </ul>
            <p style={pStyle}>The strictness of the gate is what makes the methodology defensible at audit.</p>
          </Section>

          <Divider />

          <Section n="Section 10" title="Framing Discipline">
            <p style={pStyle}>Every choice in the methodology is filtered through one principle: the assessment is investment in development, never adverse framing applied to a named individual.</p>
            <ul style={ulStyle}>
              <li style={liStyle}>No dollar amounts attached to a named candidate anywhere</li>
              <li style={liStyle}>No ROI percentages, replacement cost figures, or flight risk percentages</li>
              <li style={liStyle}>Muted colour palette throughout, no traffic lights, no weaponised colour</li>
              <li style={liStyle}>Restructuring Candidate placement rendered in muted rose, not red</li>
              <li style={liStyle}>Evidence gaps surfaced visibly rather than hidden, a "Data Unavailable" row says the assessment is being honest about what it does not know</li>
              <li style={liStyle}>No manager quotes in any candidate-facing surface</li>
              <li style={liStyle}>No candidate verbatim quotes in any candidate-facing surface</li>
            </ul>
            <p style={pStyle}>Executive-track candidates (AVP and above) are excluded from the Recommendation Layer at the methodology level. The function-fit conversation for senior leaders happens at executive level outside the assessment platform.</p>
          </Section>

          <Divider />

          <Section n="Section 11" title="What This Methodology Does Not Try to Do">
            <p style={pStyle}>Three things the methodology deliberately does not do:</p>
            <ul style={ulStyle}>
              <li style={liStyle}>It does not predict the future. No probability of leaving, no probability of success in a role the candidate has not been placed in.</li>
              <li style={liStyle}>It does not make placement decisions. Final workforce decisions are made by Sagicor leadership through Sagicor's established governance.</li>
              <li style={liStyle}>It does not collect sensitive attributes. Race, ethnicity, health, sexual orientation, gender identity, and similar attributes are not collected and do not appear in any X-Ray.</li>
            </ul>
          </Section>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 28px",
            borderTop: `1px solid ${C.line}`,
            background: "#F6F2E5",
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.5 }}>
            <div><B>Methodology version:</B> v4.1, May 2026</div>
          </div>
          <button
            data-testid="methodology-close-btn"
            onClick={onClose}
            style={{
              background: C.forest,
              color: "#FFFFFF",
              border: 0,
              borderRadius: 8,
              padding: "8px 18px",
              fontSize: 12,
              fontWeight: 600,
              fontFamily: FONT_BODY,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.forestDark; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = C.forest; }}
          >
            <X size={14} strokeWidth={2.5} /> Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────
const pStyle = { margin: "0 0 10px", fontSize: 11.5, lineHeight: 1.6, color: C.charcoal };
const ulStyle = { margin: "0 0 12px", paddingLeft: 22, fontSize: 11.5, lineHeight: 1.6, color: C.charcoal };
const subUlStyle = { margin: "6px 0 8px", paddingLeft: 16, fontSize: 11.5, lineHeight: 1.6, color: C.charcoal };
const liStyle = { marginBottom: 8 };

function Section({ n, title, children }) {
  return (
    <section style={{ marginBottom: 6 }}>
      <div style={{ fontFamily: FONT_HEAD, fontSize: 12, color: C.forestDark, fontWeight: 700, marginBottom: 2 }}>{n}</div>
      <h3 style={{ fontFamily: FONT_HEAD, fontSize: 13, color: C.charcoal, fontWeight: 700, margin: "0 0 10px" }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

function Divider() {
  return <hr style={{ border: 0, borderTop: `1px solid ${C.line}`, margin: "18px 0" }} />;
}

function B({ children }) {
  return <strong style={{ color: C.forestDark, fontWeight: 700 }}>{children}</strong>;
}
